import {
  AUDIENCE_KIND_LABELS,
  ENGAGEMENT_SOURCE_LABELS,
  ENGAGEMENT_SOURCES,
  roleHas,
  TARGET_COUNTRIES,
  WEBSITE_RULE_OPERATORS,
  type AudienceDto,
  type CreateCustomAudienceInput,
  type CreateLookalikeInput,
  type CreateSavedAudienceInput,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FileUp, Plus, RefreshCw, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AccountPicker,
  readLastAccount,
  rememberAccount,
} from "@/components/campaigns/account-picker";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { TargetingEditor } from "@/components/creation/targeting-editor";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { creationApi } from "@/lib/creation-api";
import { queries } from "@/lib/queries";
import { formatRelative } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/publicos")({ component: PublicosPage });

const TONE: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
  ok: "success",
  atencao: "warning",
  erro: "destructive",
  neutro: "secondary",
};

function size(a: AudienceDto): string {
  if (a.approximateCountLower === null && a.approximateCountUpper === null) return "—";
  const fmt = (n: number | null) =>
    n === null || n < 0 ? "?" : new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(n);
  return `${fmt(a.approximateCountLower)} – ${fmt(a.approximateCountUpper)}`;
}

function PublicosPage() {
  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();
  const workspace = useQuery(queries.workspace(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "campaigns:write") : false;
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const [account, setAccount] = useState(() => readLastAccount(workspaceId) ?? "");
  const current = account || (accounts.data?.[0]?.metaId ?? "");
  useEffect(() => {
    if (current) rememberAccount(workspaceId, current);
  }, [current, workspaceId]);
  const list = useQuery(queries.audiences(workspaceId, current));
  const [kind, setKind] = useState<"todos" | "personalizado" | "semelhante" | "salvo">("todos");
  const [dialog, setDialog] = useState<"custom" | "lookalike" | "saved" | null>(null);
  const [csvTarget, setCsvTarget] = useState<AudienceDto | null>(null);
  const [toDelete, setToDelete] = useState<AudienceDto | null>(null);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "audiences"] });

  const remove = useMutation({
    mutationFn: (metaId: string) => creationApi.deleteAudience(workspaceId, metaId),
    onSuccess: async () => {
      await invalidate();
      setToDelete(null);
      toast.success("Público excluído na Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const sync = useMutation({
    mutationFn: () =>
      fetch(`/api/workspaces/${workspaceId}/audiences/accounts/${current}?sync=1`, {
        credentials: "same-origin",
      }).then(() => undefined),
    onSuccess: async () => {
      await invalidate();
      toast.success("Sincronização iniciada.");
    },
  });

  const items = (list.data?.items ?? []).filter((a) => kind === "todos" || a.kind === kind);

  return (
    <>
      <PageHeader
        title="Públicos"
        description="Personalizados (lista, site, engajamento), semelhantes e salvos, por conta de anúncio."
        actions={
          current ? (
            <Button
              variant="outline"
              onClick={() => sync.mutate()}
              loading={sync.isPending || (list.data?.running ?? false)}
            >
              <RefreshCw />
              Atualizar
            </Button>
          ) : null
        }
      />
      {accounts.isPending ? (
        <LoadingState rows={3} />
      ) : accounts.isError ? (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : accounts.data.length === 0 ? (
        <EmptyState
          icon={<Users className="size-8" aria-hidden />}
          title="Nenhuma conta de anúncio"
          description="Conecte uma conta Meta para ver os públicos."
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <AccountPicker accounts={accounts.data} value={current} onChange={setAccount} />
            <Tabs value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <TabsList>
                <TabsTrigger value="todos">Todos</TabsTrigger>
                <TabsTrigger value="personalizado">Personalizados</TabsTrigger>
                <TabsTrigger value="semelhante">Semelhantes</TabsTrigger>
                <TabsTrigger value="salvo">Salvos</TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-xs text-muted-foreground sm:ml-auto">
              {list.data?.running
                ? "Sincronizando com a Meta…"
                : list.data?.syncedAt
                  ? `Última sincronização ${formatRelative(list.data.syncedAt)}`
                  : ""}
            </p>
          </div>
          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setDialog("custom")}>
                <Plus />
                Público personalizado
              </Button>
              <Button variant="outline" onClick={() => setDialog("lookalike")}>
                <Plus />
                Público semelhante
              </Button>
              <Button variant="outline" onClick={() => setDialog("saved")}>
                <Plus />
                Público salvo
              </Button>
            </div>
          ) : null}
          <Card>
            <CardContent>
              {list.isPending ? (
                <LoadingState rows={4} />
              ) : list.isError ? (
                <ErrorState error={list.error} onRetry={() => void list.refetch()} />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={<Users className="size-8" aria-hidden />}
                  title="Nenhum público"
                  description={
                    list.data.running
                      ? "A lista está sendo lida da Meta."
                      : "Crie o primeiro público desta conta."
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Tamanho estimado</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Atualizado</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <p className="font-medium">{a.name}</p>
                          {a.description ? (
                            <p className="text-xs text-muted-foreground">{a.description}</p>
                          ) : null}
                          <p className="text-xs text-muted-foreground">id {a.metaId}</p>
                        </TableCell>
                        <TableCell>
                          {AUDIENCE_KIND_LABELS[a.kind]}
                          <p className="text-xs text-muted-foreground">{a.subtypeLabel}</p>
                        </TableCell>
                        <TableCell>{size(a)}</TableCell>
                        <TableCell>
                          <Badge variant={TONE[a.status.tone] ?? "secondary"}>
                            {a.status.label}
                          </Badge>
                          {a.status.reason ? (
                            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                              {a.status.reason}
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {a.metaUpdatedAt ? formatRelative(a.metaUpdatedAt) : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {canWrite ? (
                            <div className="flex justify-end gap-1">
                              {a.subtype === "CUSTOM" ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setCsvTarget(a)}
                                  aria-label="Enviar lista (CSV)"
                                >
                                  <FileUp />
                                  CSV
                                </Button>
                              ) : null}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => setToDelete(a)}
                                aria-label={`Excluir ${a.name}`}
                              >
                                <Trash2 />
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {dialog === "custom" ? (
        <CustomAudienceDialog
          workspaceId={workspaceId}
          account={current}
          onClose={() => setDialog(null)}
          onCreated={(a) => setCsvTarget(a.subtype === "CUSTOM" ? a : null)}
        />
      ) : null}
      {dialog === "lookalike" ? (
        <LookalikeDialog
          workspaceId={workspaceId}
          account={current}
          audiences={list.data?.items ?? []}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "saved" ? (
        <SavedAudienceDialog
          workspaceId={workspaceId}
          account={current}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {csvTarget ? (
        <CsvDialog
          workspaceId={workspaceId}
          audience={csvTarget}
          onClose={() => setCsvTarget(null)}
        />
      ) : null}

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title={`Excluir "${toDelete?.name ?? ""}" na Meta?`}
        description="A exclusão é definitiva: anúncios que usam este público param de veicular e não podem ser retomados. Públicos semelhantes que dependem dele precisam ser excluídos antes."
        confirmLabel="Excluir na Meta"
        destructive
        loading={remove.isPending}
        onConfirm={() => (toDelete ? remove.mutate(toDelete.metaId) : undefined)}
      />
    </>
  );
}

function useCreate<T>(
  workspaceId: string,
  fn: (input: T) => Promise<AudienceDto>,
  onDone: (a: AudienceDto) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (a) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "audiences"] });
      toast.success(`Público "${a.name}" criado na Meta.`);
      onDone(a);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

function CustomAudienceDialog({
  workspaceId,
  account,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  account: string;
  onClose: () => void;
  onCreated: (a: AudienceDto) => void;
}) {
  const options = useQuery(queries.creationOptions(workspaceId, account));
  const [source, setSource] = useState<"lista" | "site" | "engajamento">("lista");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pixelId, setPixelId] = useState("");
  const [retention, setRetention] = useState("30");
  const [operator, setOperator] = useState<"i_contains" | "i_not_contains" | "eq">("i_contains");
  const [urlValue, setUrlValue] = useState("");
  const [engSource, setEngSource] = useState<(typeof ENGAGEMENT_SOURCES)[number]>("pagina");
  const [sourceId, setSourceId] = useState("");
  const create = useCreate(
    workspaceId,
    (input: CreateCustomAudienceInput) =>
      creationApi.createCustomAudience(workspaceId, account, input),
    (a) => {
      onClose();
      onCreated(a);
    },
  );
  const pages = options.data?.pages ?? [];
  const pixels = options.data?.pixels ?? [];
  const submit = () => {
    if (source === "lista") create.mutate({ source, name, description });
    else if (source === "site")
      create.mutate({
        source,
        name,
        description,
        rule: {
          pixelId,
          retentionDays: Number(retention) || 30,
          urlFilters: urlValue.trim() ? [{ operator, value: urlValue.trim() }] : [],
        },
      });
    else
      create.mutate({
        source,
        name,
        description,
        rule: {
          source: engSource,
          sourceId,
          retentionDays: Number(retention) || 365,
          videoIds: [],
        },
      });
  };
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>Novo público personalizado</DialogTitle>
            <DialogDescription>
              Lista de clientes (CSV com hash feito no servidor), visitantes do site (pixel) ou quem
              engajou.
            </DialogDescription>
          </DialogHeader>
          <Tabs value={source} onValueChange={(v) => setSource(v as typeof source)}>
            <TabsList>
              <TabsTrigger value="lista">Lista de clientes</TabsTrigger>
              <TabsTrigger value="site">Site</TabsTrigger>
              <TabsTrigger value="engajamento">Engajamento</TabsTrigger>
            </TabsList>
          </Tabs>
          <FormField id="aud-nome" label="Nome">
            <Input
              id="aud-nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
            />
          </FormField>
          <FormField id="aud-desc" label="Descrição (opcional)">
            <Input
              id="aud-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>
          {source === "lista" ? (
            <p className="text-sm text-muted-foreground">
              Depois de criar, envie o CSV (e-mail e/ou telefone). Os dados são normalizados (DDI
              55) e transformados em SHA-256 no servidor; o arquivo nunca é guardado.
            </p>
          ) : null}
          {source === "site" ? (
            <>
              <FormField id="aud-pixel" label="Pixel">
                <Select value={pixelId} onValueChange={setPixelId}>
                  <SelectTrigger id="aud-pixel">
                    <SelectValue placeholder="Escolha o pixel" />
                  </SelectTrigger>
                  <SelectContent>
                    {pixels.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <div className="grid grid-cols-[auto_1fr] gap-2">
                <Select value={operator} onValueChange={(v) => setOperator(v as typeof operator)}>
                  <SelectTrigger className="w-36" aria-label="Operador da URL">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEBSITE_RULE_OPERATORS.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        URL {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  placeholder="vazio = todos os visitantes"
                  aria-label="Trecho da URL"
                />
              </div>
              <FormField id="aud-ret" label="Retenção (dias, 1–180)">
                <Input
                  id="aud-ret"
                  type="number"
                  min={1}
                  max={180}
                  value={retention}
                  onChange={(e) => setRetention(e.target.value)}
                />
              </FormField>
            </>
          ) : null}
          {source === "engajamento" ? (
            <>
              <FormField id="aud-eng" label="Fonte">
                <Select
                  value={engSource}
                  onValueChange={(v) => {
                    setEngSource(v as typeof engSource);
                    setSourceId("");
                  }}
                >
                  <SelectTrigger id="aud-eng">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENT_SOURCES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {ENGAGEMENT_SOURCE_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                id="aud-src"
                label={engSource === "instagram" ? "Conta do Instagram" : "Página"}
              >
                <Select value={sourceId} onValueChange={setSourceId}>
                  <SelectTrigger id="aud-src">
                    <SelectValue placeholder="Escolha" />
                  </SelectTrigger>
                  <SelectContent>
                    {engSource === "instagram"
                      ? pages
                          .filter((p) => p.instagramUserId)
                          .map((p) => (
                            <SelectItem key={p.id} value={p.instagramUserId ?? ""}>
                              @{p.instagramUsername ?? p.instagramUserId}
                            </SelectItem>
                          ))
                      : pages.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField id="aud-ret2" label="Retenção (dias, 1–365)">
                <Input
                  id="aud-ret2"
                  type="number"
                  min={1}
                  max={365}
                  value={retention}
                  onChange={(e) => setRetention(e.target.value)}
                />
              </FormField>
            </>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={(source === "site" && !pixelId) || (source === "engajamento" && !sourceId)}
            >
              Criar na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LookalikeDialog({
  workspaceId,
  account,
  audiences,
  onClose,
}: {
  workspaceId: string;
  account: string;
  audiences: AudienceDto[];
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [origin, setOrigin] = useState("");
  const [country, setCountry] = useState("BR");
  const [percent, setPercent] = useState("1");
  const create = useCreate(
    workspaceId,
    (input: CreateLookalikeInput) => creationApi.createLookalike(workspaceId, account, input),
    onClose,
  );
  const seeds = audiences.filter((a) => a.kind === "personalizado");
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ name, originAudienceId: origin, country, percent: Number(percent) });
          }}
        >
          <DialogHeader>
            <DialogTitle>Novo público semelhante (lookalike)</DialogTitle>
            <DialogDescription>
              A Meta encontra pessoas parecidas com um público de origem (mínimo de 100 pessoas) num
              país.
            </DialogDescription>
          </DialogHeader>
          <FormField id="lal-nome" label="Nome">
            <Input
              id="lal-nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
            />
          </FormField>
          <FormField id="lal-origem" label="Público de origem">
            <Select value={origin} onValueChange={setOrigin}>
              <SelectTrigger id="lal-origem">
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {seeds.map((a) => (
                  <SelectItem key={a.metaId} value={a.metaId}>
                    {a.name} ({size(a)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField id="lal-pais" label="País">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger id="lal-pais">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField id="lal-pct" label="Porcentagem (1–10%)">
              <Input
                id="lal-pct"
                type="number"
                min={1}
                max={10}
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!origin}>
              Criar na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SavedAudienceDialog({
  workspaceId,
  account,
  onClose,
}: {
  workspaceId: string;
  account: string;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [targeting, setTargeting] = useState<CreateSavedAudienceInput["targeting"]>({
    countries: ["BR"],
    regions: [],
    cities: [],
    ageMin: 18,
    ageMax: 65,
    genders: [],
    interests: [],
    customAudiences: [],
    excludedCustomAudiences: [],
    advantageAudience: false,
  });
  const create = useCreate(
    workspaceId,
    (input: CreateSavedAudienceInput) =>
      creationApi.createSavedAudience(workspaceId, account, input),
    onClose,
  );
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="sm:max-w-2xl">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ name, description: "", targeting });
          }}
        >
          <DialogHeader>
            <DialogTitle>Novo público salvo</DialogTitle>
            <DialogDescription>
              Segmentação reutilizável (local, idade, gênero, interesses e públicos) para usar nos
              conjuntos.
            </DialogDescription>
          </DialogHeader>
          <FormField id="sav-nome" label="Nome">
            <Input
              id="sav-nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
            />
          </FormField>
          <TargetingEditor
            workspaceId={workspaceId}
            account={account}
            value={targeting}
            onChange={setTargeting}
            optimizationGoal="LINK_CLICKS"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={create.isPending}>
              Salvar na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CsvDialog({
  workspaceId,
  audience,
  onClose,
}: {
  workspaceId: string;
  audience: AudienceDto;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (f: File) => creationApi.uploadAudienceCsv(workspaceId, audience.metaId, f),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "audiences"] });
      setResult(
        `${r.validRows} linhas válidas de ${r.totalRows} (${r.invalidRows} sem e-mail/telefone), enviadas em ${r.batches} lote(s). A Meta recebeu ${r.numReceived} e apontou ${r.numInvalid} inválida(s). O público pode levar até 24 h para atualizar.`,
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Enviar lista para "{audience.name}"</DialogTitle>
          <DialogDescription>
            CSV com colunas <code>email</code> e/ou <code>telefone</code> (separador ; , ou tab).
            Telefones brasileiros recebem o DDI 55; tudo vira SHA-256 no servidor antes de ir para a
            Meta. O arquivo não é guardado.
          </DialogDescription>
        </DialogHeader>
        <Input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          aria-label="Arquivo CSV"
        />
        {result ? <Textarea readOnly value={result} className="min-h-16" /> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <Button
            type="button"
            disabled={!file}
            loading={send.isPending}
            onClick={() => (file ? send.mutate(file) : undefined)}
          >
            <FileUp />
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
